import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  IncomeStatementSection,
  IncomeStatementPeriodValues,
} from 'further-types/lp/general-ledger';
import { numberToCurrencyString } from 'further-ui/utils';

const EMPTY_PERIOD = { period1: 0, period2: 0, period3: 0 };

const CustomRowLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'indentation',
})<{ indentation?: number }>(({ indentation }) => ({
  paddingLeft: indentation,
}));

export function getIncomeOrExpensesCustomRow(
  baseCurrency: CurrencyCode,
  section: IncomeStatementSection,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: `total-${section.title.toLowerCase()}`,
    label: `Total ${section.title.toLowerCase()}`,
    ...section.totals,
    indentation: 16,
  });
}

export function getRealisedAndUnrealisedGainLossCustomRow(
  baseCurrency: CurrencyCode,
  section: IncomeStatementSection,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-realised-and-unrealised-gain-loss-on-investments',
    label: 'Net realised and unrealised gain / (loss) on investments',
    ...section.totals,
    indentation: 16,
  });
}

export function getOtherRealisedAndUnrealisedFxGainLossCustomRow(
  baseCurrency: CurrencyCode,
  section: IncomeStatementSection,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-other-realised-and-unrealised-gain-loss',
    label: 'Net other realised and unrealised gain / (loss)',
    ...section.totals,
    indentation: 16,
  });
}

export function getNetOperatingIncomeLossCustomRow(
  baseCurrency: CurrencyCode,
  netOperatingIncome?: IncomeStatementPeriodValues,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-operating-income-loss',
    label: 'Net operating income / (loss)',
    ...(netOperatingIncome ?? EMPTY_PERIOD),
    fontWeight: 700,
    backgroundColor: grey[100],
  });
}

export function getNetIncomeLossCustomRow(
  baseCurrency: CurrencyCode,
  netIncome?: IncomeStatementPeriodValues,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-income-loss',
    label: 'Net income / (loss)',
    ...(netIncome ?? EMPTY_PERIOD),
    fontWeight: 700,
    backgroundColor: grey[100],
  });
}

function getIncomeStatementCustomRow({
  baseCurrency,
  key,
  label,
  period1,
  period2,
  period3,
  fontWeight = undefined,
  backgroundColor = grey[50],
  indentation,
}: {
  baseCurrency: CurrencyCode;
  key: string;
  label: string;
  period1: number;
  period2: number;
  period3: number;
  fontWeight?: number;
  backgroundColor?: string;
  indentation?: number;
}) {
  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  return [
    {
      key,
      content: (
        <CustomRowLabel
          variant="h6"
          fontWeight={fontWeight}
          indentation={indentation}
        >
          {label}
        </CustomRowLabel>
      ),
      backgroundColor,
    },
    {
      key: 'period1',
      content: (
        <Typography variant="h6" fontWeight={fontWeight}>
          {formatValue(period1)}
        </Typography>
      ),
      backgroundColor,
    },
    {
      key: 'period2',
      content: (
        <Typography variant="h6" fontWeight={fontWeight}>
          {formatValue(period2)}
        </Typography>
      ),
      backgroundColor,
    },
    {
      key: 'period3',
      content: (
        <Typography variant="h6" fontWeight={fontWeight}>
          {formatValue(period3)}
        </Typography>
      ),
      backgroundColor,
    },
  ];
}
